// client/src/hooks/useContents.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAlert from './useAlert';

export const useContents = () => {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert();

    const fetchContents = useCallback(async (type = null) => {
        try {
            setLoading(true);
            setError(null);
            const url = type 
                ? `/admin/institucional/contents?type=${type}`
                : '/admin/institucional/contents';
            
            const response = await api.get(url);
            setContents(response.data.data);
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao buscar conteúdos';
            setError({ message: msg }); // Garante que o erro seja sempre um objeto
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const getContent = useCallback(async (id) => {
        try {
            const response = await api.get(`/admin/institucional/contents/${id}`);
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao buscar conteúdo');
        }
    }, []);

    const createContent = useCallback(async (contentData) => {
        try {
            const response = await api.post('/admin/institucional/contents', contentData);
            setContents(prev => [...prev, response.data.data].sort((a, b) => a.pedido - b.pedido));
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao criar conteúdo';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const updateContent = useCallback(async (id, contentData) => {
        try {
            const response = await api.put(`/admin/institucional/contents/${id}`, contentData);
            setContents(prev => prev.map(item => item._id === id ? response.data.data : item));
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao atualizar conteúdo';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const deleteContent = useCallback(async (id) => {
        try {
            await api.delete(`/admin/institucional/contents/${id}`);
            setContents(prev => prev.filter(item => item._id !== id));
            setAlert('Conteúdo excluído com sucesso!', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao excluir conteúdo';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const uploadContentImage = useCallback(async (id, file) => {
        try {
            const formData = new FormData();
            // uploadImagemMiddleware (server/middlewares/upload.js) espera o
            // campo 'imagem' — mesmo padrão de useBanners.js/uploadBannerMedia.
            formData.append('imagem', file);

            // 'Content-Type': undefined remove o header da requisição —
            // deixa o navegador detectar o FormData e montar o boundary
            // sozinho. Ver useBanners.js/uploadBannerMedia para o porquê
            // (a instância `api` tem 'application/json' como default, e
            // fixar 'multipart/form-data' manualmente aqui quebra o
            // parsing no multer por faltar o boundary).
            const response = await api.put(`/admin/institucional/contents/${id}/upload`, formData, {
                headers: { 'Content-Type': undefined }
            });

            setContents(prev => prev.map(item => item._id === id ? response.data.data : item));
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao fazer upload da imagem';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    const updateContentsOrder = useCallback(async (orderedContents) => {
        try {
            const contentsToUpdate = orderedContents.map((content, index) => ({ 
                _id: content._id, 
                order: index 
            }));
            
            await api.put('/admin/institucional/contents/update-order', { 
                contents: contentsToUpdate 
            });
            
            setContents(orderedContents.map((content, index) => ({...content, pedido: index})));
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao atualizar ordem';
            setAlert(msg, 'error');
            fetchContents(); // Recarrega para garantir consistência
            throw new Error(msg);
        }
    }, [setAlert, fetchContents]);

    useEffect(() => {
        fetchContents();
    }, [fetchContents]);

    return {
        contents,
        loading,
        error,
        fetchContents,
        getContent,
        createContent,
        updateContent,
        deleteContent,
        uploadContentImage, // ✅ Exportado para ser usado no formulário
        updateContentsOrder,
        refetch: fetchContents
    };
};